import React, { useEffect, useState } from 'react';
import api, { isSupervisorRole, getCurrentUser } from '../api/expoApi';
import HubSelector from '../components/HubSelector';

export default function BudgetVariance() {
    const user = getCurrentUser();
    const isSupervisor = isSupervisorRole(user?.role);
    const [hubId, setHubId] = useState(null);
    const [months, setMonths] = useState(6);
    const [data, setData] = useState([]);
    const [budgets, setBudgets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [tab, setTab] = useState('variance');
    const [newBudget, setNewBudget] = useState({ hub_id: '', category: '', period_month: '', budget_amount: '', notes: '' });
    const [msg, setMsg] = useState('');

    const load = async () => {
        try {
            const params = { months };
            if (hubId) params.hub_id = hubId;
            const [v, b, c] = await Promise.all([
                api.get('/budgets/variance', { params }),
                api.get('/budgets'),
                api.get('/budgets/categories')
            ]);
            setData(v.data?.data || []);
            setBudgets(b.data?.data || []);
            setCategories(c.data?.data || []);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [hubId, months]);

    const saveBudget = async () => {
        try {
            await api.post('/budgets', newBudget);
            setMsg('Budget saved'); load();
            setNewBudget({ hub_id: '', category: '', period_month: '', budget_amount: '', notes: '' });
        } catch (e) { setMsg(e.response?.data?.message || 'Error'); }
    };

    return (
        <div style={{ padding: 24 }}>
            <h1>Budget vs Actual</h1>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <HubSelector value={hubId} onChange={setHubId} />
                <label>Months: <input type="number" min="1" max="24" value={months} onChange={(e) => setMonths(e.target.value)} style={{ width: 60 }} /></label>
                <button onClick={load}>Refresh</button>
                {msg && <span style={{ color: '#198754' }}>{msg}</span>}
            </div>

            <div style={{ borderBottom: '1px solid #ddd', marginBottom: 16 }}>
                {['variance', 'budgets'].map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                        style={{
                            border: 'none', padding: '10px 16px',
                            background: tab === t ? '#fff' : 'transparent',
                            borderBottom: tab === t ? '2px solid #0d6efd' : 'none',
                            cursor: 'pointer', textTransform: 'capitalize'
                        }}>
                        {t === 'variance' ? 'Variance Report' : 'Manage Budgets'}
                    </button>
                ))}
            </div>

            {tab === 'variance' && (
                <table style={tableStyle}>
                    <thead><tr><th>Period</th><th>Hub</th><th>Category</th><th>Budget</th><th>Actual</th><th>Variance</th><th>%</th></tr></thead>
                    <tbody>
                        {data.length === 0 && <tr><td colSpan="7" style={{ textAlign: 'center', padding: 16 }}>No data</td></tr>}
                        {data.map((r, i) => {
                            const pct = Number(r.variance_pct);
                            const overspent = Number(r.actual_amount) > Number(r.budget_amount);
                            return (
                                <tr key={i}>
                                    <td>{String(r.period_month).slice(0, 7)}</td>
                                    <td>{r.hub_code}</td>
                                    <td>{r.category}</td>
                                    <td>PKR {Number(r.budget_amount).toLocaleString()}</td>
                                    <td>PKR {Number(r.actual_amount).toLocaleString()}</td>
                                    <td style={{ color: overspent ? '#dc3545' : '#198754' }}>
                                        PKR {Number(r.variance_amount).toLocaleString()}
                                    </td>
                                    <td style={{ color: Math.abs(pct) > 10 ? '#dc3545' : Math.abs(pct) > 5 ? '#fd7e14' : '#6c757d', fontWeight: 600 }}>
                                        {pct !== null ? `${pct}%` : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {tab === 'budgets' && (
                <>
                    {isSupervisor && (
                        <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 6, marginBottom: 16 }}>
                            <h3>Set Budget</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                                <select value={newBudget.hub_id} onChange={(e) => setNewBudget({...newBudget, hub_id: e.target.value})}>
                                    <option value="">Select hub...</option>
                                </select>
                                <input list="cats" placeholder="Category" value={newBudget.category} onChange={(e) => setNewBudget({...newBudget, category: e.target.value})} />
                                <datalist id="cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
                                <input type="month" placeholder="Month" value={newBudget.period_month} onChange={(e) => setNewBudget({...newBudget, period_month: e.target.value + '-01'})} />
                                <input type="number" placeholder="Amount" value={newBudget.budget_amount} onChange={(e) => setNewBudget({...newBudget, budget_amount: e.target.value})} />
                                <input placeholder="Notes" value={newBudget.notes} onChange={(e) => setNewBudget({...newBudget, notes: e.target.value})} />
                            </div>
                            <button onClick={saveBudget} style={{ marginTop: 8, padding: '8px 16px' }}>Save</button>
                        </div>
                    )}
                    <table style={tableStyle}>
                        <thead><tr><th>Period</th><th>Hub</th><th>Category</th><th>Budget</th><th>Notes</th></tr></thead>
                        <tbody>
                            {budgets.map((b) => (
                                <tr key={b.budget_id}>
                                    <td>{String(b.period_month).slice(0, 7)}</td>
                                    <td>{b.hub_code}</td>
                                    <td>{b.category}</td>
                                    <td>PKR {Number(b.budget_amount).toLocaleString()}</td>
                                    <td>{b.notes}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', background: '#fff' };
